import { useEffect, useRef, useState } from "react"
import { Controller, Resolver, useForm } from "react-hook-form"
import { FiEdit } from "react-icons/fi"
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import { EventPluginsRenderer } from "@fuse/sharedComponents/EventPluginsLoader"
import { useAlert } from "@fuse/sharedComponents/globalAlert"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import { yupResolver } from "@hookform/resolvers/yup"
import { EFeatureFlags, EVENT_CLASS } from "@letshang/constants"
import { get, isEmpty } from "lodash-es"
import moment from "moment"
import { useRecoilValue } from "recoil"
import { ROUTES } from "src/App"
import * as yup from "yup"

import { userState } from "@fuse/recoil/signup"

import { getRemoteConfigBoolean } from "@firebaseConfig/remoteConfigUtil"

import { EFailureMessage } from "@settingsAlias/constants"
import { useProcessFeatureFlags } from "@settingsAlias/featureFlags"

import { createEventWeb, onboardUserToStripe } from "@api/firebaseWrappedApis"
import { useGetAllTickets } from "@api/services/events/useEventsApi"

import {
  convertImageToBase64,
  createEventDefaultDateTime,
  ensureLetsHangHttps,
  safeJsonParse,
} from "@fuse/utils/common"

import { useTimezone } from "@fuse/customHooks/useTimezone"

import EditTicket from "@fuse/pages/eventPage/editEvent/editTicket"
import EventSetting from "@fuse/pages/eventPage/editEvent/eventSetting"
import { getTheLocationInfo } from "@fuse/pages/explore/utility/locationFunctions"
import DownloadAppModal, {
  DownloadAppModalHandle,
} from "@fuse/pages/landingPage/components/DownloadAppModal"

// Import the default background image
import eventDefaultBg from "@fuse/assets/images/event-default-bg.png"

import DateTimePickerModal, {
  DateTimePickerModalHandle,
} from "../components/dateTimePicker/DateTimePicker"
import EventPluginBottomSheet, {
  EventPluginBottomSheetHandle,
} from "../components/eventPlugins/eventPluginsBottomSheet"
import EventAddAPluginSection from "../components/eventPlugins/eventPluginsSection"
import LocationBottomSheet, {
  LocationBottomSheetHandle,
} from "../components/locationPicker/LocationBottomSheet"
import DescriptionBottomSheet, {
  DescriptionBottomSheetHandle,
} from "../createEvent/DescriptionBottomSheet"
import TicketModal from "../createEvent/TicketModal"
import FastSignUpComponent from "../FastSignUpComponent"
import useFetchEventDetails from "../useFetchEventDetails"

type EventFormData = {
  eventName: string
  startDate: string
  endDate: string
  startTime: string
  endTime: string
  location: {
    lat: number
    lon: number
    nameOfPlace: string
  }
  description: string
  flyerImage: FileList | null | string
  timeRangeError: boolean
}

const schema = yup
  .object({
    eventName: yup.string().required("Event name is required"),
    startDate: yup.string().required("Start date is required"),
    endDate: yup.string().required("End date is required"),
    startTime: yup.string().required("Start time is required"),
    endTime: yup.string().required("End time is required"),
    location: yup
      .object({
        lat: yup.number().required(),
        lon: yup.number().required(),
        nameOfPlace: yup.string().required(),
      })
      .required("Location is required"),
    description: yup.string().required("Description is required"),
    flyerImage: yup.mixed<FileList | null | string>().nullable(),
    timeRangeError: yup
      .boolean()
      .test(
        "time-range-valid",
        "End date and time must be after start date and time",
        function (value) {
          // If timeRangeError is explicitly set to true, fail validation
          if (value === true) {
            return false
          }

          // Otherwise, check the actual time range
          const { startDate, startTime, endDate, endTime } = this.parent
          if (!startDate || !startTime || !endDate || !endTime) {
            return true // Let individual field validations handle missing values
          }

          const startMoment = moment(`${startDate} ${startTime}`, [
            "MMMM DD YYYY hh:mm A",
          ])
          const endMoment = moment(`${endDate} ${endTime}`, [
            "MMMM DD YYYY hh:mm A",
          ])

          return endMoment.isAfter(startMoment)
        },
      ),
  })
  .required()

const bgImage = eventDefaultBg

const EditEvent = () => {
  const user = useRecoilValue(userState)
  const [searchParams] = useSearchParams()
  const params = useParams()
  const eventId = get(params, "eventId", "")
  const stripeToastShownRef = useRef(false)
  const { state, search, key: locationKey } = useLocation()
  const [reDirectedFrom, setReDirectedFrom] = useState("")
  const [flyerFile, setFlyerFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>(
    state?.eventDocData?.flyerImage || bgImage,
  )
  const lastValidPreviewUrl = useRef<string>(
    state?.eventDocData?.flyerImage || bgImage,
  )
  const [showMore, setShowMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showFastSignUp, setShowFastSignUp] = useState(false)
  const { showAlert } = useAlert()

  // Clean state management for back button confirmation
  const [backButtonPressed, setBackButtonPressed] = useState(false)
  const [isProgrammaticNavigation, setIsProgrammaticNavigation] =
    useState(false)

  const [createdEventId, setCreatedEventId] = useState(eventId)
  const [isCreatingEmptyEvent, setIsCreatingEmptyEvent] = useState(false)
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [activeDateTimeField, setActiveDateTimeField] = useState<
    "startDate" | "startTime" | "endDate" | "endTime"
  >("startDate")
  const [showStartDateTime, setShowStartDateTime] = useState(true)
  const isIntegrationsEnabled = useProcessFeatureFlags(
    EFeatureFlags.ENABLE_ADD_PLUGIN_TO_EVENT,
  )

  // Get the event details for editing
  const { state: eventState, refresh } = useFetchEventDetails(eventId)
  const eventDocData = eventState?.eventDetails?.eventDocData

  useEffect(() => {
    setReDirectedFrom(searchParams.get("redirectFrom"))
  }, [])

  useEffect(() => {
    if (
      reDirectedFrom === "stripe" &&
      user?.stripeAccountId &&
      !stripeToastShownRef.current
    ) {
      showSuccessToast("Stripe account connected successfully")
      stripeToastShownRef.current = true
    }
  }, [reDirectedFrom, user])

  const ticketedEventsWebFeatureFlag = useProcessFeatureFlags(
    EFeatureFlags.TICKETED_EVENTS_WEB,
  )
  const dateTimePickerModalRef = useRef<DateTimePickerModalHandle>(null)
  const onOpenDateTimePickerModal = () => {
    setIsDatePickerModalOpen(true)
    dateTimePickerModalRef.current?.openModal()
  }

  const descriptionBottomSheetRef = useRef<DescriptionBottomSheetHandle>(null)
  const onOpenDescriptionBottomSheet = () => {
    descriptionBottomSheetRef.current?.openModal()
  }

  const eventPluginBottomSheetRef = useRef<EventPluginBottomSheetHandle>(null)

  const { timezone } = useTimezone()
  const navigate = useNavigate()

  const [showEditTicket, setShowEditTicket] = useState(false)
  const [isDatePickerModalOpen, setIsDatePickerModalOpen] = useState(false)
  const [templateState, setTemplateState] = useState(() => {
    const saved = JSON.parse(localStorage.getItem("savedEventDetails") || "{}")
    if (!isEmpty(saved)) localStorage.removeItem("savedEventDetails")
    return state?.template || saved
  })

  useEffect(() => {
    if (showEditTicket || showTicketModal) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [showEditTicket, showTicketModal])

  // Push initial history entry once on mount
  useEffect(() => {
    // Only push if we don't already have page protection
    if (!window.history.state?.pageProtection) {
      window.history.pushState(
        { pageProtection: true },
        "",
        window.location.href,
      )
    }
  }, []) // No dependencies - runs only once

  // Add back button protection for page navigation (not modal navigation)
  useEffect(() => {
    const handleBackNavigation = (event: PopStateEvent) => {
      // Skip if this is programmatic navigation (from handleCancelPress)
      if (isProgrammaticNavigation) {
        setIsProgrammaticNavigation(false) // Reset flag
        return
      }

      const hasActiveModals =
        showEditTicket || showTicketModal || showFastSignUp

      // Don't interfere if modals are currently open
      if (hasActiveModals) {
        return
      }

      // Don't interfere if navigating TO a modal state (this shouldn't trigger page leave)
      if (
        event.state?.bottomSheetOpen ||
        event.state?.ticketModalOpen ||
        event.state?.editTicketModalOpen ||
        event.state?.ticketPriceInfoModalOpen ||
        event.state?.dateTimePickerModalOpen
      ) {
        return
      }

      // Don't interfere if navigating FROM a modal state (let modal handle it first)
      if (
        window.history.state?.bottomSheetOpen ||
        window.history.state?.ticketModalOpen ||
        window.history.state?.editTicketModalOpen ||
        window.history.state?.ticketPriceInfoModalOpen ||
        window.history.state?.dateTimePickerModalOpen
      ) {
        return
      }

      // Prevent the back navigation and show popup
      event.preventDefault()

      if (!backButtonPressed) {
        setBackButtonPressed(true)
        showAlert(
          "Are you sure you want to leave?",
          "You have unsaved changes that will be lost.",
          "Yes, Leave",
          () => {
            setBackButtonPressed(false)
            setIsProgrammaticNavigation(true) // Set flag before navigation
            // Use the same navigation approach as the working cancel button
            handleCancelPress()
          },
          "Keep Editing",
          () => {
            setBackButtonPressed(false) // Reset if they cancel
            // Push a new history entry so the next back press can be intercepted
            window.history.pushState(
              { pageProtection: true },
              "",
              window.location.href,
            )
          },
        )
      }
    }

    window.addEventListener("popstate", handleBackNavigation)

    return () => {
      window.removeEventListener("popstate", handleBackNavigation)
    }
  }, [
    backButtonPressed,
    isProgrammaticNavigation,
    showEditTicket,
    showTicketModal,
    showFastSignUp,
  ])

  const { initialStartDate, initialEndDate, initialStartTime, initialEndTime } =
    createEventDefaultDateTime()

  const queryParams = new URLSearchParams(search)
  const referenceFrom = queryParams.get("reference")

  useEffect(() => {
    if (createdEventId) {
      refresh()
    }
  }, [createdEventId, showEditTicket])

  // Show force signup modal if user is not logged in
  useEffect(() => {
    if (!user?.isLoggedIn || isEmpty(state?.eventDocData)) {
      navigate({
        pathname: ROUTES.EVENTS.replace(":eventId", createdEventId),
      })
      // setShowFastSignUp(true)
    }
  }, [user])

  useEffect(() => {
    let newPreviewUrl = lastValidPreviewUrl.current

    if (flyerFile) {
      const objectUrl = URL.createObjectURL(flyerFile)
      newPreviewUrl = objectUrl
      setPreviewUrl(objectUrl)

      // Cleanup the object URL when component unmounts or file changes
      return () => URL.revokeObjectURL(objectUrl)
    } else if (templateState?.flyerImage) {
      newPreviewUrl = templateState.flyerImage
      setPreviewUrl(templateState.flyerImage)
    } else if (state?.eventDocData?.flyerImage) {
      newPreviewUrl = state.eventDocData.flyerImage
      setPreviewUrl(state.eventDocData.flyerImage)
    } else if (lastValidPreviewUrl.current !== bgImage) {
      // Keep the last valid preview URL if it's not the default image
      setPreviewUrl(lastValidPreviewUrl.current)
    } else {
      newPreviewUrl = bgImage
      setPreviewUrl(bgImage)
    }

    // Update the ref with the new value
    lastValidPreviewUrl.current = newPreviewUrl
  }, [flyerFile, state?.eventDocData, templateState])

  const flyerImageUrl = previewUrl

  const countryCode = get(user, "country", "US")
  const [returnURL, setReturnURL] = useState("")

  const onConnectToStripe = async () => {
    setIsLoading(true)
    try {
      let toBeSavedEventDetails = {}

      if (createdEventId && eventState.eventDetails) {
        toBeSavedEventDetails = {
          ...eventState.eventDetails.eventDocData,
          eventId: createdEventId,
          eventName: formState.eventName,
          eventDescription: formState.description,
        }
      } else {
        toBeSavedEventDetails = { ...formState, flyerImage: "" }
      }

      await localStorage.setItem(
        "savedEventDetails",
        JSON.stringify(toBeSavedEventDetails),
      )
      const params = `?redirectFrom=stripe`
      const returnURL = `${window.location.origin}/create-event-v3${params}`

      let stripeURL = ""

      await onboardUserToStripe({
        countryCode: countryCode,
        returnURL: returnURL,
        refreshURL: returnURL,
      })
        .then((data) => {
          const isSuccess = get(data, "success", true)

          if (!isSuccess) {
            const errorMessage = get(
              data,
              "message",
              EFailureMessage.UserAlreadyConnectWithStripe,
            )

            console.log("error Message", errorMessage, data)
            setIsLoading(false)
            showErrorToast(errorMessage)
            return
          }
          const error = get(data, "error", null)
          if (error) {
            // hideLoader()
            setIsLoading(false)
            showErrorToast(EFailureMessage.UnableToConnectWithStripe)
            return
          }
          setIsLoading(false)

          stripeURL = get(data, "url", "")
          window.open(stripeURL, "_self")

          // navigate(stripeURL)
          // hideLoader()
        })
        .catch(() => {
          setIsLoading(false)
          showErrorToast(EFailureMessage.UnableToConnectWithStripe)
        })
    } catch (e) {
      showErrorToast("Something went wrong.")
      setIsLoading(false)
    }
  }

  function convertToUTC(dateTimeStr: string) {
    return moment(dateTimeStr, "MMMM DD YYYY hh:mm A").utc().toISOString()
  }

  const checkImageType = async (file: FileList | string) => {
    if (typeof file === "string") {
      // Handle URLs
      if (file.startsWith("http")) {
        return file
      }
      // Handle imported assets
      else if (file === bgImage) {
        const response = await fetch(bgImage)
        const blob = await response.blob()
        const base64String = await convertImageToBase64(blob)
        if (typeof base64String === "string") {
          return base64String.split(",")[1]
        }
        // For imported assets, we'll return null to indicate no upload is needed
        // The frontend will use the imported asset directly
        // return "https://firebasestorage.googleapis.com/v0/b/walkme-e7896.appspot.com/o/event%20assets%2F1.png?alt=media&token=a45a8b60-1c42-4cb6-bfef-b78aba693a5c"
      }
      // Handle base64 strings
      else {
        return file.split(",")[1]
      }
    } else {
      // Handle FileList objects
      const base64String = await convertImageToBase64(file)
      if (typeof base64String === "string") {
        return base64String.split(",")[1]
      }
      return ""
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
    control,
    watch,
    setValue,
  } = useForm<EventFormData>({
    resolver: yupResolver(schema) as unknown as Resolver<EventFormData>,
    defaultValues: {
      eventName:
        state?.eventDocData?.eventName || templateState?.eventName || "",
      startDate: state?.eventDocData?.timing
        ? moment(state?.eventDocData?.timing?.eventDate).format("MMMM DD YYYY")
        : templateState?.timing?.startDate,
      endDate: state?.eventDocData?.timing
        ? moment(state?.eventDocData?.timing?.eventEndDate).format(
            "MMMM DD YYYY",
          )
        : templateState?.timing?.endDate,
      startTime: state?.eventDocData?.timing
        ? moment(state?.eventDocData?.timing?.eventStartTime).format("hh:00 A")
        : templateState?.timing?.startTime,
      endTime: state?.eventDocData?.timing
        ? moment(state?.eventDocData?.timing?.eventEndTime).format("hh:00 A")
        : templateState?.timing?.endTime,
      location: {
        lat: state?.eventDocData?.location?.latitude
          ? state?.eventDocData?.location?.latitude
          : templateState?.location?.latitude,
        lon: state?.eventDocData?.location?.longitude
          ? state?.eventDocData?.location?.longitude
          : templateState?.location?.longitude,
        nameOfPlace:
          state?.eventDocData?.location?.eventLocation ||
          templateState?.location?.nameOfPlace,
      },
      description:
        state?.eventDocData?.eventDescription ||
        templateState?.eventDescription ||
        "",
      flyerImage:
        state?.eventDocData?.flyerImage || templateState?.flyerImage || bgImage,
      timeRangeError: false,
    },
  })

  const formState = watch()
  const endDate = watch("endDate")
  const endTime = watch("endTime")
  const startDate = watch("startDate")
  const startTime = watch("startTime")
  const description = watch("description")
  const location = watch("location")

  const handleDateTimeConfirm = ({
    startDate,
    startTime,
    endDate,
    endTime,
    activeField,
    timeRangeError,
  }: {
    startDate: string
    startTime: string
    endDate: string
    endTime: string
    activeField: "startDate" | "startTime" | "endDate" | "endTime"
    timeRangeError?: boolean
  }) => {
    setValue("startDate", startDate, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue("startTime", startTime, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue("endDate", endDate, { shouldValidate: true, shouldDirty: true })
    setValue("endTime", endTime, { shouldValidate: true, shouldDirty: true })
    if (timeRangeError !== undefined) {
      setValue("timeRangeError", timeRangeError, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
    setActiveDateTimeField(activeField)
  }

  const locationBottomSheetRef = useRef<LocationBottomSheetHandle>(null)
  const onOpenLocationBottomSheet = () => {
    locationBottomSheetRef.current?.openModal()
  }

  const { data: ticketsData } = useGetAllTickets(createdEventId)
  const tickets = get(ticketsData, "data.tickets", [])

  const eventDetails = eventState?.eventDetails || state?.eventDetails
  const minTicketPrice = get(eventDetails, "minTicketPrice", 0)
  const maxTicketPrice = get(eventDetails, "maxTicketPrice", 0)
  const isSingleTicket = minTicketPrice === maxTicketPrice

  const onSubmit = async (data: EventFormData) => {
    setIsLoading(true)
    const payloadData: any = {}
    payloadData.eventClass =
      tickets.length > 0
        ? EVENT_CLASS.TICKETED_EVENT
        : EVENT_CLASS.NON_TICKETED_EVENT
    if (dirtyFields.eventName || dirtyFields.description) {
      payloadData.eventName = data?.eventName
      payloadData.eventDescription = data?.description
    }
    if (createdEventId) {
      payloadData.eventId = createdEventId
    }
    if (formState?.flyerImage !== state?.eventDocData?.flyerImage) {
      const finalFlyerImage = await checkImageType(formState?.flyerImage)
      payloadData.flyerImage = finalFlyerImage
    }
    if (
      dirtyFields.startDate ||
      dirtyFields.endDate ||
      dirtyFields.startTime ||
      dirtyFields.endTime
    ) {
      payloadData.timing = {
        eventDate: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        eventEndDate: convertToUTC(
          `${formState?.endDate} ${formState?.endTime}`,
        ),
        timeZone: "UTC",
        eventStartTime: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        eventEndTime: convertToUTC(
          `${formState?.endDate} ${formState?.endTime}`,
        ),
        signupCloseDate: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        signupCloseTime: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        displayTimezone: timezone,
      }
    }
    if (dirtyFields.timeRangeError) {
      payloadData.timeRangeError = formState?.timeRangeError
    }
    if (dirtyFields.location) {
      payloadData.location = {
        eventLocation: formState?.location?.nameOfPlace,
        latitude: formState?.location?.lat,
        longitude: formState?.location?.lon,
        guestLocationPreview: await getTheLocationInfo({
          latitude: formState?.location?.lat,
          longitude: formState?.location?.lon,
        }),
      }
    }
    await createEventWeb({ ...payloadData })
      .then(async (res: any) => {
        if (res?.success) {
          showSuccessToast(
            `Event updated successfully.${
              dirtyFields.location ||
              dirtyFields.startDate ||
              dirtyFields.endDate ||
              dirtyFields.startTime ||
              dirtyFields.endTime
                ? " Your guests will be notified."
                : ""
            }`,
          )
          navigate(`${ROUTES.EVENTS.replace(":eventId", res.data.eventId)}`, {
            replace: true, // Replace the current history entry
          })
          setIsLoading(false)
        } else {
          showErrorToast(
            res?.message || "Something went wrong, please try again later",
          )
          setIsLoading(false)
        }
      })
      .catch((err) => {
        showErrorToast(
          err?.message || "Something went wrong while saving the draft",
        )
        setIsLoading(false)
      })
  }

  const modalRef = useRef<DownloadAppModalHandle>(null)

  const onOpenDownloadAppModal = () => {
    modalRef.current?.openModal()
  }

  const handleDescriptionChange = (value: string) => {
    setValue("description", value, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const handleLocationChange = (value: any) => {
    setValue("location", value.location, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const handleSubmitWithValidation = () => {
    // This will run validation before submitting
    handleSubmit(
      (data) => {
        onSubmit(data)
      },
      (errors) => {
        // This callback runs when there are validation errors
        setIsLoading(false)
        console.log("Form errors:", errors)

        // Generic error message
        if (errors.timeRangeError) {
          showErrorToast("End date and time must be after start date and time")
        } else {
          showErrorToast("Missing required fields")
        }
      },
    )()
  }

  // Helper: check if user info or stripe is missing
  const isProfileIncomplete =
    !user?.email ||
    !(user?.firstName || user?.name) ||
    !user?.dateOfBirth ||
    !user?.stripeAccountId

  // Handler for Tickets button
  const handleTicketsClick = () => {
    setShowMore(false)
    setShowEditTicket(true)
  }

  // Handler for Complete Profile arrow
  const handleCompleteProfile = () => {
    setShowEditTicket(false)
    navigate(ROUTES.COMPLETE_PROFILE)
  }

  const handleCancelPress = () => {
    const canGoBack = locationKey !== "default"

    if (state?.template) {
      navigate(`${ROUTES.EVENT_TEMPLATES}?category=${state.category}`)
      return
    }

    if (canGoBack) {
      // Smart navigation: if we're on the "protected" version of the page,
      // navigate(-1) would just remove protection but stay on same page.
      // So use navigate(-2) to actually go back to the previous page.
      if (window.history.state?.pageProtection) {
        navigate(-2)
      } else {
        navigate(-1)
      }
      return
    }

    if (user?.isLoggedIn || isEmpty(referenceFrom)) {
      navigate(ROUTES.LANDING)
    } else {
      window.location.href = ensureLetsHangHttps(referenceFrom)
    }
  }

  const handleDateTimeFieldClick = (isStart: boolean) => {
    // Reset the active field to the date view
    setActiveDateTimeField(isStart ? "startDate" : "endDate")
    setShowStartDateTime(isStart)
    setIsDatePickerModalOpen(true)
    dateTimePickerModalRef.current?.openModal()
  }

  if (showMore) {
    return (
      <EventSetting
        flyerImageUrl={flyerImageUrl}
        onBack={() => setShowMore(false)}
        eventId={createdEventId}
        onTicketsClick={handleTicketsClick}
      />
    )
  }

  return (
    <BackgroundWrapper
      bgImage={flyerImageUrl}
      className={`min-h-[100vh] h-full relative touch-manipulation`}
    >
      {isLoading && <LoadingSpinner />}
      <BlurWrapper
        blurKey={get(blurKeys, "mainBlurView")}
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className=" bg-gray-400/10 h-[10vh] flex flex-row  items-center px-6  top-0 left-0 w-full z-10 fixed"
      >
        <div
          className="text-white/30 text-base cursor-pointer"
          onClick={handleCancelPress}
        >
          Cancel
        </div>
        <div className="text-white text-base cursor-pointer flex-1">
          Update event
        </div>
        <div
          className="text-[#30D158] text-base cursor-pointer"
          onClick={handleSubmitWithValidation}
        >
          Update
        </div>
      </BlurWrapper>

      <BlurWrapper
        blurKey={get(blurKeys, "mainBlurView")}
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="min-h-[100vh] h-full flex flex-col overflow-auto relative"
      >
        <div className="flex flex-col justify-center items-center p-6 w-full max-w-2xl mx-auto mt-[10vh]">
          <form
            className="w-full space-y-6 flex flex-col justify-center items-center"
            onSubmit={(e) => {
              e.preventDefault()
              handleSubmitWithValidation()
            }}
          >
            <div className="space-y-2 relative ">
              <img
                src={flyerImageUrl}
                alt="Flyer"
                className="h-52 w-56 rounded-lg object-cover"
              />

              <BlurWrapper
                overlayOpacityDefault="30"
                overlayColorDefault="dark"
                className="h-10 w-10 rounded-full flex justify-center items-center absolute bottom-2 right-2"
                onClick={() => {
                  setReDirectedFrom(null)
                  document.getElementById("flyerImageInput")?.click()
                }}
              >
                <div>
                  <FiEdit className="text-white text-lg" />
                </div>
                <input
                  id="flyerImageInput"
                  type="file"
                  accept="image/jpeg,image/png"
                  {...register("flyerImage")}
                  onClick={() => {}}
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null
                    if (file) {
                      const fileType = file.type.toLowerCase()
                      if (
                        fileType !== "image/jpeg" &&
                        fileType !== "image/png"
                      ) {
                        alert("Please select only JPG or PNG images")
                        e.target.value = ""
                        return
                      }
                    }
                    setFlyerFile(file)
                    setValue("flyerImage", e.target.files[0] as any)
                  }}
                  className="hidden"
                />
              </BlurWrapper>
            </div>
            <div
              className={`space-y-2 w-full rounded-2xl bg-white/10  p-4 ${errors.eventName ? "border-2 border-red-400" : ""}`}
              style={{
                border: errors.eventName
                  ? undefined
                  : "1px solid rgba(255, 255, 255, 0.50)",
              }}
            >
              <input
                {...register("eventName")}
                className="w-full rounded-lg bg-transparent text-white focus:outline-none text-2xl"
                placeholder="Name your event"
              />

              {/* <hr className="w-full border-white/30" />
              <div className="flex flex-row gap-4 items-center pt-3">
                <img
                  src={user?.avatarUrl}
                  alt="Flyer"
                  className="h-8 w-8 rounded-full"
                />
                <span className="flex flex-col justify-center items-center text-base text-white/50">
                  by{" "}
                  {user?.name ||
                    user?.displayName ||
                    user?.firstName + " " + user?.lastName}
                </span>
              </div> */}
            </div>

            <div
              className={`space-y-2 w-full rounded-2xl p-4 bg-white/10 ${
                // Only highlight container if multiple fields are missing
                errors.startDate ||
                errors.endDate ||
                errors.startTime ||
                errors.endTime ||
                errors.location ||
                errors.timeRangeError
                  ? "border-2 border-red-400"
                  : ""
              }`}
              style={{
                border:
                  errors.startDate ||
                  errors.endDate ||
                  errors.startTime ||
                  errors.endTime ||
                  errors.location ||
                  errors.timeRangeError
                    ? undefined
                    : "1px solid rgba(255, 255, 255, 0.50)",
              }}
            >
              <div
                className="flex flex-row gap-4 items-center py-2"
                onClick={onOpenDateTimePickerModal}
              >
                <p className="text-2xl">🗓️</p>

                <input
                  type="text"
                  className="w-full rounded-lg bg-transparent text-white  focus:outline-none"
                  placeholder="Date and time"
                  value={`${startDate}, ${startTime}`}
                />
              </div>
              <hr className="w-full border-white/10" />
              <div className="">
                <div className={`flex rounded-xl w-full`}>
                  <Controller
                    name="location"
                    control={control}
                    render={({ field }) => (
                      <div
                        className="flex py-2 items-center  w-full"
                        onClick={() => {
                          onOpenLocationBottomSheet()
                        }}
                      >
                        <div className="">
                          <p className="text-2xl">📍</p>
                        </div>
                        <div className=" ml-2 flex flex-1 items-center w-full">
                          <div className="flex-1 w-full">
                            <div className="flex justify-between w-full">
                              <input
                                className="bg-transparent w-full focus:outline-none text-white ml-2 text-ellipsis "
                                placeholder="Location"
                                value={location?.nameOfPlace || ""}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>
              <>
                <hr className="w-full border-white/10 my-0" />
                <div
                  className="flex flex-row items-start py-3 cursor-pointer"
                  onClick={() => {
                    if (
                      eventDocData.eventClass === EVENT_CLASS.NON_TICKETED_EVENT
                    ) {
                      showInfoToast("You event is live", {
                        text2:
                          "To add tickets, please create a new event and add atleast one ticket before going live.",
                      })
                      return
                    }
                    if (!isEmpty(tickets)) {
                      setShowEditTicket(true)
                    } else {
                      showInfoToast("Fetching tickets. Please try again later.")
                    }
                  }}
                >
                  <span
                    className="text-2xl mr-3 flex-shrink-0 flex items-center h-full"
                    role="img"
                    aria-label="ticket"
                  >
                    💵
                  </span>
                  <div className="flex flex-col items-start">
                    {eventDetails?.numberOfTicketsByInitiator > 0 ? (
                      <>
                        <span className="text-base text-white font-normal">
                          {isSingleTicket
                            ? minTicketPrice === 0
                              ? "Free"
                              : `$${minTicketPrice}`
                            : minTicketPrice === 0 && maxTicketPrice === 0
                              ? "Free"
                              : minTicketPrice === 0
                                ? `Free - $${maxTicketPrice}`
                                : `$${minTicketPrice} - $${maxTicketPrice}`}
                        </span>
                        <span className="text-xs text-white/50 mt-1 text-left">
                          {eventDetails?.numberOfTicketsByInitiator} ticket
                          {eventDetails?.numberOfTicketsByInitiator !== 1
                            ? "s"
                            : ""}{" "}
                          created
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-base text-white/50 font-normal">
                          Add tickets (optional)
                        </span>
                        <span className="text-xs italic text-white/25 mt-1 text-left">
                          Tickets can only be created before going live
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </>
            </div>

            <div
              className={`space-y-2 w-full rounded-2xl p-4 bg-white/10 ${errors.description ? "border-2 border-red-400" : ""}`}
              style={{
                border: errors.description
                  ? undefined
                  : "1px solid rgba(255, 255, 255, 0.50)",
              }}
              onClick={onOpenDescriptionBottomSheet}
            >
              <textarea
                // {...register("description")}
                className="w-full  bg-transparent text-white min-h-[150px] text-base focus:outline-none resize-none"
                placeholder="Describe your event"
                value={description}
              />
            </div>

            {/* Plugins Section */}
            {isIntegrationsEnabled && eventId && eventDocData && (
              <div className="w-full">
                {/* Show existing plugins */}
                <EventPluginsRenderer
                  eventDocData={eventDocData}
                  eventId={eventId}
                  user={user}
                  isAdmin={true}
                  unCachedLocation={{}}
                />

                {/* Add new plugins section */}
                <div
                  className="mt-0 sm:mt-0 p-4 rounded-2xl w-full cursor-pointer bg-white/10"
                  style={{
                    border: "1px solid rgba(255, 255, 255, 0.50)",
                  }}
                >
                  <EventAddAPluginSection
                    eventId={eventId}
                    bottomSheetRef={eventPluginBottomSheetRef}
                    // backgroundStyle={{
                    //   containerClassName:
                    //     "mt-6 sm:mt-6 bg-white/10 border-white/20 border p-4 rounded-2xl w-full cursor-pointer",
                    //   searchClassName:
                    //     "flex gap-2 items-center justify-center bg-white/10 rounded-lg p-3 w-full",
                    // }}
                    backgroundStyle={{
                      containerClassName:
                        "mt-0 sm:mt-0 p-0 rounded-none w-full cursor-pointer bg-transparent border-none",
                    }}
                    user={user}
                  />
                </div>
              </div>
            )}

            {/* Guest List Component */}
            {/* <div className="space-y-2 w-full rounded-2xl p-4 bg-white/10 ">
              <div className="text-white/50 text-lg mb-4 text-left">
                Guest list
              </div>
              <div className="flex flex-row py-2 pr-6 overflow-hidden ">
                {Array(8)
                  .fill(0)
                  .map((_, index) => (
                    <div
                      key={index}
                      className={`w-16 h-16 rounded-full bg-white/10 flex-shrink-0 flex items-center justify-center -ml-5 first:ml-0  relative z-[${index + 1}]`}
                    >
                      <FaUser className="text-black/20" size={20} />
                    </div>
                  ))}
              </div>
            </div> */}
          </form>

          {/* <div
            className="flex flex-col gap-2 items-center bg-white/10 rounded-2xl p-4 w-full mt-8 cursor-pointer"
            onClick={() => {
              setShowMore(true)
            }}
          >
            <p>⚙️</p>
            <p className="text-white text-lg">More</p>
          </div> */}
        </div>
      </BlurWrapper>
      <DateTimePickerModal
        ref={dateTimePickerModalRef}
        startDate={startDate}
        startTime={startTime}
        endDate={endDate}
        endTime={endTime}
        onConfirm={handleDateTimeConfirm}
        setIsDatePickerModalOpen={setIsDatePickerModalOpen}
      />

      <DescriptionBottomSheet
        ref={descriptionBottomSheetRef}
        register={register}
        errors={errors}
        description={description}
        onChange={handleDescriptionChange}
      />

      <LocationBottomSheet
        onOpenAdvancedSettings={onOpenDownloadAppModal}
        ref={locationBottomSheetRef}
        value={{ location: location }}
        onLocationSelected={handleLocationChange}
      />
      <DownloadAppModal ref={modalRef} />
      <FastSignUpComponent
        isOpen={showFastSignUp}
        setOpenState={setShowFastSignUp}
        title="Let's get you started"
        showTitleAsIs={true}
        cannotDismissPopup={true}
      />

      <EditTicket
        isOpen={showEditTicket}
        onClose={() => setShowEditTicket(false)}
        eventId={createdEventId}
        localTickets={tickets}
      />
      <TicketModal
        isOpen={showTicketModal}
        onClose={() => setShowTicketModal(false)}
        eventId={createdEventId}
      />
      <EventPluginBottomSheet
        ref={eventPluginBottomSheetRef}
        eventId={eventId}
        onEventCreated={(eventId) => {
          // In edit mode, we don't need to update the event ID since it already exists
          console.log("Event created in edit mode:", eventId)
        }}
      />
    </BackgroundWrapper>
  )
}

export default EditEvent
